import { useId, type ReactNode } from "react";
import { cn } from "./utils/cn";

export interface RadioOption<T extends string = string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<T extends string = string> {
  name: string;
  value: T;
  onChange: (value: T) => void;
  options: RadioOption<T>[];
  /** Layout direction (not a style variant). */
  direction?: "row" | "col";
  disabled?: boolean;
  className?: string;
}

/** Canonical radio group. Supersedes common/RadioButtons. */
function RadioGroup<T extends string = string>({
  name,
  value,
  onChange,
  options,
  direction = "row",
  disabled,
  className,
}: RadioGroupProps<T>) {
  const groupId = useId();

  return (
    <div
      role="radiogroup"
      className={cn(
        "flex",
        direction === "row" ? "flex-wrap gap-6 mobile:gap-3" : "flex-col gap-3",
        className
      )}
    >
      {options.map((option) => {
        const id = `${groupId}-${option.value}`;
        const isDisabled = disabled || option.disabled;
        return (
          <div key={option.value} className="flex items-center gap-2">
            <input
              id={id}
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={isDisabled}
              onChange={() => onChange(option.value)}
              className="h-[18px] w-[18px] cursor-pointer text-secondary accent-secondary focus-visible:ring focus-visible:ring-offset-0"
            />
            <label
              htmlFor={id}
              className={cn(
                "cursor-pointer text-menu",
                isDisabled && "cursor-not-allowed opacity-60"
              )}
            >
              {option.label}
            </label>
          </div>
        );
      })}
    </div>
  );
}

export default RadioGroup;
